import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import whatsappService, { BroadcastHistory, BroadcastStats } from '../services/whatsappService';

const WhatsappBroadcastHistory: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Data
  const [history, setHistory] = useState<BroadcastHistory[]>([]);
  const [stats, setStats] = useState<BroadcastStats | null>(null);
  const [summary, setSummary] = useState<Record<string, number>>({});

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchFilter, setSearchFilter] = useState('');

  // Dialog
  const [selectedMessage, setSelectedMessage] = useState<BroadcastHistory | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Expand details
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadHistory();
    loadStats();
  }, [page, statusFilter]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const response = await whatsappService.getBroadcastHistory(page, limit, statusFilter);
      setHistory(response.history);
      setTotalPages(response.pagination.totalPages);
      setTotal(response.pagination.total);
      setSummary(response.summary);
    } catch (error) {
      console.error('Error loading broadcast history:', error);
      setError('Gagal memuat riwayat broadcast');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await whatsappService.getBroadcastStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error loading broadcast stats:', error);
    }
  };

  const handleViewMessage = (message: BroadcastHistory) => {
    setSelectedMessage(message);
    setDialogOpen(true);
  };

  const handleCancelBroadcast = async (queueId: number) => {
    if (!window.confirm('Apakah Anda yakin ingin membatalkan pesan broadcast ini?')) {
      return;
    }

    try {
      await whatsappService.cancelBroadcast(queueId);
      setSuccess('Pesan broadcast berhasil dibatalkan');
      loadHistory();
      loadStats();
    } catch (error: any) {
      console.error('Error cancelling broadcast:', error);
      setError(error.response?.data?.message || 'Gagal membatalkan broadcast');
    }
  };

  const toggleRowExpansion = (id: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircleIcon fontSize="small" />;
      case 'pending':
        return <ScheduleIcon fontSize="small" />;
      case 'failed':
        return <ErrorIcon fontSize="small" />;
      case 'cancelled':
        return <CancelIcon fontSize="small" />;
      default:
        return <ScheduleIcon fontSize="small" />;
    }
  };

  const filteredHistory = history.filter((item) => {
    if (!searchFilter) return true;
    const q = searchFilter.toLowerCase();
    return (
      (item.phone || '').toLowerCase().includes(q) ||
      (item.message || '').toLowerCase().includes(q)
    );
  });

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Riwayat WhatsApp Broadcast
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Broadcast
              </Typography>
              <Typography variant="h5">
                {stats.totalBroadcasts.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Menunggu
              </Typography>
              <Typography variant="h5" color="warning.main">
                {stats.pendingBroadcasts.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Terkirim
              </Typography>
              <Typography variant="h5" color="success.main">
                {stats.sentBroadcasts.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Gagal
              </Typography>
              <Typography variant="h5" color="error.main">
                {stats.failedBroadcasts.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, alignItems: 'center' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setPage(1);
                  setStatusFilter(e.target.value);
                }}
                label="Status"
              >
                <MenuItem value="all">Semua Status</MenuItem>
                <MenuItem value="pending">Menunggu</MenuItem>
                <MenuItem value="sent">Terkirim</MenuItem>
                <MenuItem value="failed">Gagal</MenuItem>
                <MenuItem value="cancelled">Dibatalkan</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ gridColumn: { xs: '1', sm: '1', md: 'span 2' } }}>
              <TextField
                fullWidth
                size="small"
                label="Cari nomor/pesan"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Masukkan nomor WhatsApp atau kata kunci pesan"
              />
            </Box>
            <Button
              variant="outlined"
              onClick={() => {
                loadHistory();
                loadStats();
              }}
              startIcon={<RefreshIcon />}
              disabled={loading}
              fullWidth
            >
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* History Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width="50px"></TableCell>
                      <TableCell>Nomor</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Prioritas</TableCell>
                      <TableCell>Dijadwalkan</TableCell>
                      <TableCell>Dikirim</TableCell>
                      <TableCell>Percobaan</TableCell>
                      <TableCell width="120px">Aksi</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredHistory.map((item) => (
                      <React.Fragment key={item.id}>
                        <TableRow>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => toggleRowExpansion(item.id)}
                            >
                              {expandedRows.has(item.id) ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>{item.phone}</TableCell>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(item.status)}
                              label={item.status}
                              color={getStatusColor(item.status) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`P${item.priority}`}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {format(new Date(item.scheduled_at), 'dd/MM/yy HH:mm', { locale: id })}
                          </TableCell>
                          <TableCell>
                            {item.sent_at 
                              ? format(new Date(item.sent_at), 'dd/MM/yy HH:mm', { locale: id })
                              : '-'
                            }
                          </TableCell>
                          <TableCell>
                            {item.retry_count} / {item.max_retries || 3}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Lihat Pesan">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewMessage(item)}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {item.status === 'pending' && (
                                <Tooltip title="Batalkan">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCancelBroadcast(item.id)}
                                    color="error"
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                            <Collapse in={expandedRows.has(item.id)} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 1 }}>
                                <Typography variant="subtitle2" gutterBottom component="div">
                                  Detail Pesan
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                                  <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                                    {item.message}
                                  </Typography>
                                </Paper>
                                {item.error_message && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="subtitle2" color="error" gutterBottom>
                                      Error:
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'error.50' }}>
                                      <Typography variant="body2" color="error">
                                        {item.error_message}
                                      </Typography>
                                    </Paper>
                                  </Box>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                  Dibuat: {format(new Date(item.createdAt), 'dd MMMM yyyy, HH:mm', { locale: id })}
                                  {item.related_id && ` | ID Terkait: ${item.related_id}`}
                                </Typography>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, newPage) => setPage(newPage)}
                    color="primary"
                  />
                </Box>
              )}

              {filteredHistory.length === 0 && !loading && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    Tidak ada riwayat broadcast ditemukan
                  </Typography>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Message Detail Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Detail Pesan Broadcast
        </DialogTitle>
        <DialogContent>
          {selectedMessage && (
            <Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="subtitle2">Nomor WhatsApp:</Typography>
                  <Typography variant="body1">{selectedMessage.phone}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Status:</Typography>
                  <Chip
                    icon={getStatusIcon(selectedMessage.status)}
                    label={selectedMessage.status}
                    color={getStatusColor(selectedMessage.status) as any}
                    size="small"
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2">Prioritas:</Typography>
                  <Typography variant="body1">Level {selectedMessage.priority}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Percobaan:</Typography>
                  <Typography variant="body1">
                    {selectedMessage.retry_count} dari {selectedMessage.max_retries || 3}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Dijadwalkan:</Typography>
                  <Typography variant="body1">
                    {format(new Date(selectedMessage.scheduled_at), 'dd MMMM yyyy, HH:mm', { locale: id })}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Dikirim:</Typography>
                  <Typography variant="body1">
                    {selectedMessage.sent_at 
                      ? format(new Date(selectedMessage.sent_at), 'dd MMMM yyyy, HH:mm', { locale: id })
                      : 'Belum dikirim'
                    }
                  </Typography>
                </Box>
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                Isi Pesan:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                  {selectedMessage.message}
                </Typography>
              </Paper>

              {selectedMessage.error_message && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="error" gutterBottom>
                    Pesan Error:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'error.50' }}>
                    <Typography variant="body2" color="error">
                      {selectedMessage.error_message}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Tutup</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsappBroadcastHistory;
